import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Manages a piece of state which temporarily transitions to a new value for a specified duration,
 * before automatically reverting back to its original (steady) state.
 */
export const useTransientState = <V>(
  steadyState: V,
  duration: number
): [unknown, (value: V) => void] => {
  const [state, setState] = useState(steadyState)
  const timerRef = useRef<number>()

  const setTransientState = useCallback((newValue: V) => {
    window.clearTimeout(timerRef.current)
    timerRef.current = undefined
    setState(newValue)
  }, [])

  useEffect(() => {
    if (state !== steadyState) {
      timerRef.current = window.setTimeout(
        () => setState(steadyState),
        duration
      )
    }

    return () => window.clearTimeout(timerRef.current)
  }, [state, steadyState, duration])

  return [state, setTransientState]
}
